// The "not connected" state of the mail surface — 0 accessible accounts
// (architecture/MAIL.md §3.1). Same layout as ChatWelcomePrompt, but with a
// real glyph instead of an ambient watermark: the envelope IS the message
// here, so it carries `title` ink, not a de-emphasised wash. No badges, no
// borders, no shadow.
//
// P1 (MAIL-UI-CONNECT-SETTINGS-1): MailSetupPanel mounts below the glyph and
// IS the body once the §3.2a gate has answered. The generic poster lines above
// it are the pre-answer state only — two headings saying the same thing in
// different words is the defect the panel was written to remove, so they step
// aside the moment the server names a state.
export const MailEmptyState = {
  flex: '1 1 0',
  minHeight: '0',
  flow: 'y',
  // ⚠️ `justify-content: center` CLIPS an overflowing column at BOTH ends and
  // gives no way to reach the lost part — measured live on /mail 2026-09-05:
  // with the admin `steps` cards mounted, the panel ran 1203px inside an 854px
  // pane and the heading sat at -268px, unreachable (MailMain is
  // `overflow: hidden`, so no ancestor scrolled either). `safe center` centres
  // while it fits and falls back to start-aligned the moment it does not, and
  // `overflow: auto` gives this box its own scroll. `alignItems` is written
  // out rather than folded into the `align` shorthand because the two axes no
  // longer take the same value.
  alignItems: 'center',
  justifyContent: 'safe center',
  overflow: 'auto',
  gap: 'B',
  // `E` (110px) of inset was right for three lines of poster copy; with the
  // gate panel below it, it is a third of the pane before the first word.
  padding: 'C',
  color: 'caption',

  // `flexShrink: 0` is load-bearing, not decoration: a flex child with no
  // intrinsic size shrinks along the MAIN axis, so the moment the panel below
  // makes this column overflow (an admin's six `steps` cards do), the glyph
  // collapsed to height 0 and the surface lost its only image — measured live
  // on /mail 2026-09-05.
  Icon: { name: 'email', boxSize: 'D D', color: 'title', flexShrink: '0' },
  Heading: {
    // FA515 arming: without the declared selector this `if:` short-circuits on
    // `s.root` and never subscribes to `mailSetup`, so the poster line would
    // stay on screen beside the panel once the gate answers.
    ifDeps: [(el, s) => s.root && s.root.mailSetup],
    if: (el, s) => !(s.root && s.root.mailSetup),
    tag: 'h2',
    fontSize: 'C',
    fontWeight: '700',
    color: 'title',
    margin: '0',
    textAlign: 'center',
    text: '{{ mail.empty.heading | polyglot }}'
  },
  Body: {
    ifDeps: [(el, s) => s.root && s.root.mailSetup],
    if: (el, s) => !(s.root && s.root.mailSetup),
    tag: 'p',
    fontSize: 'Z2',
    color: 'caption',
    textAlign: 'center',
    margin: '0',
    maxWidth: 'H',
    lineHeight: '1.5',
    text: '{{ mail.empty.body | polyglot }}'
  },

  // The six-state gate (§3.2a). Its box is centred; its own text stays left,
  // which is what a multi-line instruction plus a row of actions needs.
  MailSetupPanel: {}
}
